import { t } from "../../i18n";
import type { RejectedFormUpdate } from "../../services/ChatService";
import type { ApplyResult, PageForms, RejectedUpdate, RejectionDetail, RejectionReason } from "./forms";
import { entityLabel, getPageContext } from "./pageContext";

const REJECTION_REASON_DEFAULTS: Record<RejectionReason, string> = {
  stale: "the form is no longer on the page",
  "unknown-path": "no such field on the form",
  excluded: "protected field, edit it by hand",
  "read-only": "read-only field",
  truncated: "value too long to be shown whole, edit it by hand",
  invalid: "value rejected by validation",
  unsupported: "field kind not supported",
};

const REJECTION_DETAIL_DEFAULTS: Record<RejectionDetail, string> = {
  "not-integer": "must be an integer",
  "not-number": "must be a number",
  "not-boolean": "must be true or false",
  "no-options": "the field lists no options",
  "not-an-option": "not one of the options",
  "not-an-array": "must be a list of values",
  "not-a-string": "every element must be text",
  "not-a-date-time": "must be an ISO-8601 date-time with offset",
  "not-a-single-value": "must be a single value, not a list",
  "unsettable-kind": "this kind of field cannot be set",
  validator: "refused by the field's '{{validator}}' validator",
};

function nonBlank(value: string | null | undefined): string | undefined {
  return value?.trim() ? value : undefined;
}

// Preferred over the server's copy of the label, which is escaped for the prompt.
function sentLabel(sentForms: PageForms | undefined, formId: string | null | undefined, path: string | null | undefined): string | undefined {
  const form = sentForms?.forms.find((candidate) => candidate.formId === formId);
  return nonBlank(form?.fields.find((field) => field.path === path)?.label);
}

function fieldName(...candidates: (string | null | undefined)[]): string {
  return candidates.map(nonBlank).find((candidate) => candidate !== undefined) ?? t("ai-chat.forms.unnamed-field", undefined, "(unnamed field)");
}

function serverRejectionLine(sentForms: PageForms | undefined, rejection: RejectedFormUpdate): string {
  return `${fieldName(sentLabel(sentForms, rejection.formId, rejection.path), rejection.label, rejection.path)}: ${rejection.reason}`;
}

function browserRejectionLine(sentForms: PageForms | undefined, rejection: RejectedUpdate): string {
  const { update } = rejection;
  const name = fieldName(rejection.label, sentLabel(sentForms, update.formId, update.path), update.path);
  const reason = t(`ai-chat.forms.reason-${rejection.reason}`, undefined, REJECTION_REASON_DEFAULTS[rejection.reason]);
  const detail =
    rejection.detail && t(`ai-chat.forms.detail-${rejection.detail}`, { validator: rejection.validator }, REJECTION_DETAIL_DEFAULTS[rejection.detail]);
  return detail ? `${name}: ${reason} (${detail})` : `${name}: ${reason}`;
}

/**
 * The note shown under a reply: what the turn actually did to the page's forms. The reply's prose
 * can claim changes that never happened, so the note is built only from what the server refused
 * and what the browser applied or refused. A turn sent with a forms snapshot always gets one, so
 * "nothing changed" is stated rather than left to be inferred; a turn sent without one gets a
 * note only when something was rejected.
 */
export function turnOutcomeNote(
  sentForms: PageForms | undefined,
  result: ApplyResult | undefined,
  serverRejected: RejectedFormUpdate[] | null | undefined,
): string | undefined {
  const applied = result?.applied ?? [];
  const rejectedLines = [
    ...(serverRejected ?? []).map((rejection) => serverRejectionLine(sentForms, rejection)),
    ...(result?.rejected ?? []).map((rejection) => browserRejectionLine(sentForms, rejection)),
  ];
  const lines: string[] = [];
  if (applied.length > 0) {
    lines.push(t("ai-chat.forms.applied", undefined, "Applied:"), ...applied.map((change) => `${change.label} → ${change.displayValue}`));
  } else if (sentForms) {
    lines.push(t("ai-chat.forms.no-change", undefined, "No fields were changed"));
  }
  if (rejectedLines.length > 0) {
    lines.push(t("ai-chat.forms.not-applied", undefined, "Not applied:"), ...rejectedLines);
  }
  return lines.length > 0 ? lines.join("\n") : undefined;
}

/**
 * The note's line for the reply's navigation request, from what the panel did with it. A reply can
 * say it opened a page while carrying no navigateTo; such a turn gets no line, so an "Opening …"
 * reply without "Opened …" under it was never followed.
 */
export function navigationOutcomeLine(navigateTo: string | null | undefined, followed: boolean): string | undefined {
  if (!navigateTo) {
    return undefined;
  }
  const page = getPageContext(navigateTo);
  const target = page ? `${entityLabel(page.entityType)} ${page.entityId}` : navigateTo;
  // The note renders as React text, so i18next's HTML escaping would show "/" as "&#x2F;".
  const params = { target, interpolation: { escapeValue: false } };
  return followed ? t("ai-chat.navigation.opened", params, "Opened {{target}}") : t("ai-chat.navigation.not-opened", params, "Not opened: {{target}}");
}
